import { Formik } from 'formik';
import { createTestAreasSchema } from 'validators/createTrialSchemas';
import TestAreasForm from 'components/create-trial/TestAreasForm';
import { useAppSelector } from 'redux/hooks';
import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { PlotSizingCalculator } from 'utils/PlotSizingCalculator';

import { StepProps } from '../../types/stepFlow.type';

const TestAreasContainer = ({ children }: StepProps) => {
  const { trial } = useAppSelector((state) => state.createTrial);
  const [suggestedValues, setSuggestedValues] = useState({
    width_ft: 120,
    length_ft: 300,
    buffer_ft: 120,
    plot_buffer_length: 0,
    plot_buffer_width: 0,
  });

  // Track which fields have been manually modified by the user
  const [userModifiedFields, setUserModifiedFields] = useState<Set<string>>(
    new Set(),
  );

  // Track if we've already applied suggestions to avoid re-applying
  const appliedSuggestionsRef = useRef<Set<string>>(new Set());

  // Track previous suggested values to detect changes
  const prevSuggestedRef = useRef(suggestedValues);

  const [fieldValidation, setFieldValidation] = useState<{
    plotWidthValid: boolean;
    plotLengthValid: boolean;
    plotWidthError?: string;
    plotLengthError?: string;
    bufferValid: boolean;
    bufferError?: string;
    plotBufferValid: boolean;
    plotBufferError?: string;
    plotBufferWidthValid: boolean;
    plotBufferWidthError?: string;
    plotBufferLengthValid: boolean;
    plotBufferLengthError?: string;
    suggestedDimensions?: {
      plotWidthFt?: number;
      plotLengthFt?: number;
      bufferFt?: number;
    };
  } | null>(null);

  const formikRef = useRef<any>(null);

  useEffect(() => {
    // Calculate suggested values using equipment information
    const calculateSuggestions = () => {
      try {
        // Check if we have the required equipment data
        const hasEquipmentData =
          trial.execution_equipment_swath_width &&
          trial.harvester_swath_width &&
          trial.execution_equipment_dgps_type &&
          trial.harvester_dgps_type;

        if (!hasEquipmentData) {
          // Use defaults if equipment data is missing
          return;
        }

        // Build PlotSizingCalculator config from trial data
        const config = {
          executionEquipmentSwathWidth: Number(
            trial.execution_equipment_swath_width,
          ),
          executionEquipmentSwathWidthUnits:
            trial.execution_equipment_swath_width_units as 'ft' | 'm',
          executionEquipmentRateChangeDistance:
            Number(trial.execution_equipment_rate_change_distance) || 0,
          executionEquipmentRateChangeDistanceUnits:
            trial.execution_equipment_rate_change_distance_units as 'ft' | 'm',
          executionEquipmentCanSplitSwath:
            trial.execution_equipment_can_split_swath || false,
          executionEquipmentDgpsAccuracy:
            Number(trial.execution_equipment_dgps_accuracy) || 3,
          executionEquipmentDgpsAccuracyUnits:
            trial.execution_equipment_dgps_units as 'ft' | 'm' | 'cm' | 'in',
          harvesterSwathWidth: Number(trial.harvester_swath_width),
          harvesterSwathWidthUnits: trial.harvester_swath_width_units as
            | 'ft'
            | 'm',
          harvesterSpatialResolution:
            Number(trial.harvester_spatial_resolution) || 0,
          harvesterSpatialResolutionUnits:
            trial.harvester_spatial_resolution_units as 'ft' | 'm',
          harvesterDgpsAccuracy: Number(trial.harvester_dgps_accuracy) || 3,
          harvesterDgpsAccuracyUnits: trial.harvester_dgps_units as
            | 'ft'
            | 'm'
            | 'cm'
            | 'in',
          treatmentDrift: Number(trial.treatment_drift_distance) || 0,
          treatmentDriftUnits: trial.treatment_drift_units as 'ft' | 'm',
          maxLikelyHarvestAngle: Number(trial.max_likely_harvest_angle) || 0,
        };

        const calculator = new PlotSizingCalculator(config);
        const results = calculator.calculatePlotSizing();

        const newSuggestions = {
          width_ft: Math.round(results.suggestedPlotWidthFt),
          length_ft: Math.round(results.suggestedPlotLengthFt),
          buffer_ft: Math.round(results.suggestedFieldBufferWidthFt),
          plot_buffer_length: Math.round(results.suggestedPlotBufferLengthFt),
          plot_buffer_width: Math.round(results.suggestedPlotBufferWidthFt),
        };

        setSuggestedValues(newSuggestions);
      } catch (error) {
        // Keep default values on error
      }
    };

    calculateSuggestions();
  }, [trial]);

  // Auto-apply suggestions when they change, but only for unmodified fields
  useEffect(() => {
    if (!formikRef.current) return;

    const { values, setFieldValue } = formikRef.current;
    const fieldsToUpdate: Array<{ fieldName: string; value: number }> = [];

    // Check each field to see if we should auto-apply
    (
      [
        'width_ft',
        'length_ft',
        'buffer_ft',
        'plot_buffer_length',
        'plot_buffer_width',
      ] as const
    ).forEach((fieldName) => {
      const currentValue = String(values[fieldName] || '').trim();
      const suggestedValue = suggestedValues[fieldName];
      const prevSuggested = prevSuggestedRef.current[fieldName];

      // If Redux has a value for this field, the user set it - don't override
      const reduxValue = trial[fieldName];
      const hasReduxValue =
        reduxValue !== undefined && reduxValue !== null && reduxValue !== '';

      const isEmptyOrDefault =
        currentValue === '' ||
        currentValue === null ||
        currentValue === undefined;
      const userModified = userModifiedFields.has(fieldName);
      const suggestionChanged = suggestedValue !== prevSuggested;
      const alreadyApplied = appliedSuggestionsRef.current.has(fieldName);

      // Hard-coded defaults that should be replaced with suggestions
      const hardDefaults = {
        width_ft: 120,
        length_ft: 300,
        buffer_ft: 120,
        plot_buffer_length: null, // 0 is a valid user choice, not a default
        plot_buffer_width: null, // 0 is a valid user choice, not a default
      };

      const isHardDefault = Number(currentValue) === hardDefaults[fieldName];

      // Apply suggestion if:
      // 1. Field is empty/default AND user hasn't modified it AND Redux doesn't have a value
      // OR
      // 2. Field has hard-coded default AND user hasn't modified it AND Redux doesn't have a value
      // OR
      // 3. Suggestion changed AND field currently has the old suggestion AND user hasn't modified it since AND Redux doesn't have a value
      const shouldApply =
        !hasReduxValue &&
        ((!userModified && isEmptyOrDefault) ||
          (!userModified && isHardDefault) ||
          (suggestionChanged &&
            !userModified &&
            !alreadyApplied &&
            Number(currentValue) === prevSuggested));

      if (shouldApply) {
        fieldsToUpdate.push({ fieldName, value: suggestedValue });
        appliedSuggestionsRef.current.add(fieldName);
      }
    });

    // Apply all updates in batch to avoid multiple renders
    if (fieldsToUpdate.length > 0) {
      fieldsToUpdate.forEach(({ fieldName, value }) => {
        setFieldValue(fieldName, value);
      });
    }

    // Update previous suggestions reference
    prevSuggestedRef.current = { ...suggestedValues };
  }, [suggestedValues, userModifiedFields, trial]);

  // Handle field changes to track user modifications
  const handleFieldChange = (fieldName: string, value: any) => {
    // Mark this field as user-modified when user actually changes it
    // (not when we auto-apply suggestions)

    // Always mark as user-modified if the user provides any explicit value
    // This includes 0, which is a valid user choice that should be preserved
    if (value !== '' && value != null && value !== undefined) {
      setUserModifiedFields((prev) => new Set(prev).add(fieldName));
    }
  };

  // Create dynamic validation schema with current field validation
  const validationSchema = useMemo(() => {
    return createTestAreasSchema(fieldValidation);
  }, [fieldValidation]);

  // Trigger validation when fieldValidation changes
  useEffect(() => {
    if (fieldValidation && formikRef.current) {
      // Manually validate the fields that might have errors
      formikRef.current.validateField('width_ft');
      formikRef.current.validateField('length_ft');
      formikRef.current.validateField('buffer_ft');
      formikRef.current.validateField('plot_buffer_length');
      formikRef.current.validateField('plot_buffer_width');
    }
  }, [fieldValidation]);

  // Define validation callback
  const handleValidationChange = useCallback(
    (validation) => {
      setFieldValidation(validation);

      // Auto-apply suggested dimensions when validation fails
      if (validation.suggestedDimensions && formikRef.current) {
        const { setFieldValue } = formikRef.current;
        const { suggestedDimensions } = validation;

        // Only auto-apply if the field is currently invalid and hasn't been user-modified
        if (
          !validation.plotWidthValid &&
          suggestedDimensions.plotWidthFt &&
          !userModifiedFields.has('width_ft')
        ) {
          setFieldValue('width_ft', suggestedDimensions.plotWidthFt);
        }

        if (
          !validation.plotLengthValid &&
          suggestedDimensions.plotLengthFt &&
          !userModifiedFields.has('length_ft')
        ) {
          setFieldValue('length_ft', suggestedDimensions.plotLengthFt);
        }

        if (
          !validation.bufferValid &&
          suggestedDimensions.bufferFt &&
          !userModifiedFields.has('buffer_ft')
        ) {
          setFieldValue('buffer_ft', suggestedDimensions.bufferFt);
        }
      }
    },
    [userModifiedFields],
  );

  // Calculate AB line GeoJSON at component level
  const ablineGeoJSON = useMemo(() => {
    // Use adjusted geojson if available, otherwise use original
    let geojson = trial.abline_adjusted_geojson || trial.abline_geojson;

    // If no geojson but we have a heading, recreate the geojson
    if (!geojson && trial.abline_heading && trial.field_boundary_geojson) {
      try {
        const {
          createABLineFromHeading,
          createABLineGeoJSON,
        } = require('utils/ShapefileUtils');
        const fieldBoundary =
          typeof trial.field_boundary_geojson === 'string'
            ? JSON.parse(trial.field_boundary_geojson)
            : trial.field_boundary_geojson;
        const heading = parseFloat(trial.abline_heading);
        const coordinates = createABLineFromHeading(heading, fieldBoundary);
        geojson = createABLineGeoJSON(coordinates);
      } catch (err) {
        // Failed to recreate AB line from heading, geojson will remain null
      }
    }

    const finalGeoJSON =
      typeof geojson === 'string' ? JSON.parse(geojson) : geojson;
    return finalGeoJSON;
  }, [
    trial.abline_adjusted_geojson,
    trial.abline_geojson,
    trial.abline_heading,
    trial.field_boundary_geojson,
  ]);

  return (
    <Formik
      initialValues={{
        trial_type: trial.trial_type || 'plot',
        length_ft: trial.length_ft || '',
        width_ft: trial.width_ft || '',
        buffer_ft: trial.buffer_ft || '',
        plot_buffer_length: trial.plot_buffer_length || 0,
        plot_buffer_width: trial.plot_buffer_width || 0,
        apply_trial_buffer_to_field_boundary:
          trial.apply_trial_buffer_to_field_boundary ?? true,
      }}
      enableReinitialize={false}
      onSubmit={() => {}}
      validationSchema={validationSchema}
      validateOnMount
      validateOnChange
      validateOnBlur={false}
    >
      {(formik) => {
        // Store formik reference for external validation triggering
        formikRef.current = formik;

        const { values } = formik;

        return (
          <>
            <TestAreasForm
              fieldBoundaryGeoJSON={trial.field_boundary_geojson}
              trialBoundaryGeoJSON={
                trial.trial_boundary_is_field_boundary
                  ? undefined
                  : trial.trial_boundary_geojson
              }
              ablineGeoJSON={ablineGeoJSON}
              plotWidthFt={
                values.width_ft !== '' &&
                values.width_ft !== null &&
                values.width_ft !== undefined
                  ? Number(values.width_ft)
                  : suggestedValues.width_ft
              }
              plotLengthFt={
                values.length_ft !== '' &&
                values.length_ft !== null &&
                values.length_ft !== undefined
                  ? Number(values.length_ft)
                  : suggestedValues.length_ft
              }
              bufferFt={
                values.buffer_ft !== '' &&
                values.buffer_ft !== null &&
                values.buffer_ft !== undefined
                  ? Number(values.buffer_ft)
                  : suggestedValues.buffer_ft
              }
              plotBufferLengthFt={Number(values.plot_buffer_length) || 0}
              plotBufferWidthFt={Number(values.plot_buffer_width) || 0}
              isPlot={values.trial_type === 'plot'}
              treatmentRates={trial.treatment_rates}
              nominalRate={trial.nominal_rate}
              applicationUnits={trial.application_units}
              onPlotValidationChange={handleValidationChange}
              onFieldChange={handleFieldChange}
              suggestedValues={suggestedValues}
              userModifiedFields={userModifiedFields}
            />
            {children}
          </>
        );
      }}
    </Formik>
  );
};

export default TestAreasContainer;
